/* Sutera Dawn runtime compatibility layer. */
(function () {
  'use strict';

  var win = window;
  var doc = document;

  win.theme = win.theme || {};
  var theme = win.theme;

  function storageSupported(type) {
    try {
      var store = type === 'session' ? win.sessionStorage : win.localStorage;
      var key = '__sutera_storage_test__';
      store.setItem(key, key);
      store.removeItem(key);
      return true;
    } catch (e) {
      return false;
    }
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  function throttle(fn, delay) {
    var last = 0;
    return function () {
      var now = Date.now();
      if (now - last < delay) return;
      last = now;
      return fn.apply(this, arguments);
    };
  }

  function toArray(items) {
    if (!items) return [];
    if (items instanceof Element) return [items];
    if (typeof items.length === 'number') return Array.prototype.slice.call(items);
    return [];
  }

  function dur(value) {
    if (value == null) return 0;
    var n = Number(value);
    if (!isFinite(n)) return 0;
    return n > 10 ? n : n * 1000;
  }

  if (!win.Motion) {
    win.Motion = {
      animate: function (targets, props, opts) {
        var elements = toArray(targets);
        var duration = dur(opts && opts.duration);
        var delay = opts && opts.delay;

        var done = Promise.all(
          elements.map(function (el, i) {
            return new Promise(function (resolve) {
              var d = 0;
              if (typeof delay === 'function') d = dur(delay(i));
              else if (delay != null) d = dur(delay);

              setTimeout(function () {
                if (typeof props === 'function') {
                  props(1);
                  resolve();
                  return;
                }

                Object.keys(props || {}).forEach(function (k) {
                  var v = props[k];
                  var out = Array.isArray(v) ? v[v.length - 1] : v;
                  if (!(el instanceof HTMLElement)) return;
                  if (k === 'y') {
                    el.style.transform = 'translateY(' + (typeof out === 'number' ? out + 'px' : out) + ')';
                  } else {
                    try {
                      el.style[k] = out;
                    } catch (e) {}
                  }
                });
                resolve();
              }, d + duration);
            });
          })
        );

        return { finished: done };
      },
      timeline: function (steps) {
        var chain = Promise.resolve();
        (steps || []).forEach(function (step) {
          chain = chain.then(function () {
            return win.Motion.animate(step[0], step[1], step[2]).finished;
          });
        });
        return { finished: chain };
      },
      inView: function (target, callback, options) {
        var nodes = toArray(target);
        if (!nodes.length || typeof callback !== 'function') return function () {};
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              callback(entry.target);
              io.unobserve(entry.target);
            });
          },
          { rootMargin: (options && options.margin) || '0px' }
        );

        nodes.forEach(function (n) {
          io.observe(n);
        });
        return function () {
          io.disconnect();
        };
      },
      stagger: function (step) {
        var s = Number(step) || 0;
        return function (i) {
          return s * i;
        };
      },
      scroll: function () {
        return { stop: function () {} };
      },
      ScrollOffset: { Enter: 'enter', Exit: 'exit' },
    };
  }

  theme.config = Object.assign(
    {
      rtl: doc.documentElement.getAttribute('dir') === 'rtl',
      isTouch: 'ontouchstart' in win || navigator.maxTouchPoints > 0,
      motionReduced: win.matchMedia('(prefers-reduced-motion: reduce)').matches,
      mediaQuerySmall: 'screen and (max-width: 767px)',
      mqlSmall: false,
      hasSessionStorage: storageSupported('session'),
      hasLocalStorage: storageSupported('local'),
    },
    theme.config || {}
  );

  theme.routes = theme.routes || {};
  theme.settings = theme.settings || {};
  theme.strings = theme.strings || {};
  theme.dateStrings = theme.dateStrings || {};
  theme.variantStrings = theme.variantStrings || {};
  theme.quickOrderListStrings = theme.quickOrderListStrings || {};

  theme.safeDefine = function (name, ctor, options) {
    if (!name || !ctor || customElements.get(name)) return;
    try {
      customElements.define(name, ctor, options);
    } catch (e) {}
  };

  theme.DOMready =
    theme.DOMready ||
    function (cb) {
      if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', cb, { once: true });
      else cb();
    };

  theme.utils = Object.assign(
    {
      debounce: debounce,
      throttle: throttle,
      sectionId: function (el) {
        var section =
          el && (el.closest('[id^="shopify-section-"]') || el.closest('[data-section-id]') || el.closest('[data-id]'));
        if (!section) return '';
        if (section.dataset.sectionId) return section.dataset.sectionId;
        if (section.dataset.id) return section.dataset.id;
        if (section.id && section.id.indexOf('shopify-section-') === 0) return section.id.replace('shopify-section-', '');
        return section.id || '';
      },
      fetchConfig: function (type, method) {
        return {
          method: method || 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/' + (type || 'json'),
          },
        };
      },
      postLink2: function (path, options) {
        options = options || {};
        var params = options.parameters || {};
        var form = doc.createElement('form');
        form.method = options.method || 'post';
        form.action = path;

        Object.keys(params).forEach(function (key) {
          var input = doc.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = typeof params[key] === 'object' ? JSON.stringify(params[key]) : String(params[key]);
          form.appendChild(input);
        });

        doc.body.appendChild(form);
        form.submit();
        form.remove();
      },
      imageLoaded: function (img) {
        return new Promise(function (resolve) {
          if (!img || img.complete) resolve();
          else {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          }
        });
      },
      waitForEvent: function (target, eventName) {
        return new Promise(function (resolve) {
          target.addEventListener(eventName, resolve, { once: true });
        });
      },
      shopifyEvent: function (event, element, callback) {
        if (!event || !element || typeof callback !== 'function') return;
        var section = element.closest('.shopify-section');
        if (!section || section.contains(event.target)) callback(event);
      },
      setScrollbarWidth: function () {
        var width = win.innerWidth - doc.documentElement.clientWidth;
        doc.documentElement.style.setProperty('--scrollbar-width', Math.max(0, width) + 'px');
      },
      externalLinksNewTab: function () {
        if (!theme.settings.externalLinksNewTab) return;
        doc.querySelectorAll('a[href^="http"]').forEach(function (a) {
          if (a.hostname !== location.hostname && !a.hasAttribute('target')) {
            a.target = '_blank';
            a.rel = 'noopener';
          }
        });
      },
    },
    theme.utils || {}
  );

  theme.getElementWidth =
    theme.getElementWidth ||
    function (element, value) {
      var canvas = doc.createElement('canvas');
      var ctx = canvas.getContext('2d');
      var styles = win.getComputedStyle(element);
      ctx.font = styles.fontWeight + ' ' + styles.fontSize + ' ' + styles.fontFamily;
      return Math.ceil(ctx.measureText(value || '').width) + 24;
    };

  theme.a11y = theme.a11y || {};
  theme.a11y.trapFocus =
    theme.a11y.trapFocus ||
    function (container, focus) {
      if (typeof win.trapFocus === 'function') win.trapFocus(container, focus || container);
    };
  theme.a11y.removeTrapFocus =
    theme.a11y.removeTrapFocus ||
    function (focus) {
      if (typeof win.removeTrapFocus === 'function') win.removeTrapFocus(focus || null);
    };

  if (typeof theme.cookiesEnabled !== 'boolean') theme.cookiesEnabled = navigator.cookieEnabled;

  theme.Currency = theme.Currency || {};
  if (typeof theme.Currency.formatMoney !== 'function') {
    theme.Currency.formatMoney = function (cents, format) {
      if (cents == null) return '';
      var value = typeof cents === 'string' ? cents.replace('.', '') : cents;
      var amount = Number(value) / 100;
      var fmt = format || '${{amount}}';
      var decimals = fmt.indexOf('amount_no_decimals') > -1 ? 0 : 2;
      var n = amount.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
      return fmt
        .replace('{{amount_no_decimals}}', amount.toLocaleString(undefined, { maximumFractionDigits: 0 }))
        .replace('{{amount}}', n)
        .replace('{{ amount_no_decimals }}', amount.toLocaleString(undefined, { maximumFractionDigits: 0 }))
        .replace('{{ amount }}', n);
    };
  }

  function makePubSub() {
    var subs = {};
    var events = {
      cartUpdate: 'cartUpdate',
      quantityUpdate: 'quantityUpdate',
      quantityBoundries: 'quantityBoundries',
      quantityRules: 'quantityRules',
      optionValueSelectionChange: 'optionValueSelectionChange',
      variantChange: 'variantChange',
      cartError: 'cartError',
      facetUpdate: 'facetUpdate',
    };

    function subscribe(eventName, cb) {
      if (!subs[eventName]) subs[eventName] = [];
      subs[eventName].push(cb);
      return function () {
        subs[eventName] = (subs[eventName] || []).filter(function (fn) {
          return fn !== cb;
        });
      };
    }

    function publish(eventName, payload, fromBridge) {
      (subs[eventName] || []).forEach(function (fn) {
        try {
          fn(payload);
        } catch (e) {
          console.error(e);
        }
      });

      if (fromBridge || typeof win.publish !== 'function' || !win.PUB_SUB_EVENTS) return;
      if (eventName === events.cartUpdate) {
        win.publish(win.PUB_SUB_EVENTS.cartUpdate, {
          source: (payload && payload.source) || 'theme-compat',
          cartData: (payload && (payload.cartData || payload.cart)) || payload,
          productVariantId: payload && payload.productVariantId,
        });
      } else if (eventName === events.variantChange) {
        win.publish(win.PUB_SUB_EVENTS.variantChange, payload || {});
      } else if (eventName === events.optionValueSelectionChange) {
        win.publish(win.PUB_SUB_EVENTS.optionValueSelectionChange, payload || {});
      } else if (eventName === events.cartError) {
        win.publish(win.PUB_SUB_EVENTS.cartError, payload || {});
      }
    }

    return { PUB_SUB_EVENTS: events, subscribe: subscribe, publish: publish };
  }

  if (!theme.pubsub || typeof theme.pubsub.subscribe !== 'function' || typeof theme.pubsub.publish !== 'function') {
    theme.pubsub = makePubSub();
  } else {
    theme.pubsub.PUB_SUB_EVENTS = Object.assign(makePubSub().PUB_SUB_EVENTS, theme.pubsub.PUB_SUB_EVENTS || {});
  }

  if (!win.__sutera_pubsub_bridge && typeof win.subscribe === 'function' && win.PUB_SUB_EVENTS) {
    win.__sutera_pubsub_bridge = true;
    win.subscribe(win.PUB_SUB_EVENTS.cartUpdate, function (p) {
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, p, true);
    });
    win.subscribe(win.PUB_SUB_EVENTS.variantChange, function (p) {
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.variantChange, p, true);
    });
    win.subscribe(win.PUB_SUB_EVENTS.optionValueSelectionChange, function (p) {
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, p, true);
    });
    win.subscribe(win.PUB_SUB_EVENTS.cartError, function (p) {
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, p, true);
    });
  }

  if (!theme.initWhenVisible) {
    theme.initWhenVisible = class ScriptLoader {
      constructor(callback, delay) {
        this.callback = callback;
        this.triggered = false;
        this.events = ['click', 'mousemove', 'keydown', 'touchstart', 'touchmove', 'wheel'];
        this.onTrigger = this.onTrigger.bind(this);

        this.events.forEach(
          function (name) {
            win.addEventListener(name, this.onTrigger, { passive: true });
          }.bind(this)
        );

        this.timeout = setTimeout(this.onTrigger, Number(delay) || 5000);
      }

      onTrigger() {
        if (this.triggered) return;
        this.triggered = true;
        if (typeof this.callback === 'function') this.callback();

        this.events.forEach(
          function (name) {
            win.removeEventListener(name, this.onTrigger, { passive: true });
          }.bind(this)
        );
        clearTimeout(this.timeout);
      }
    };
  }

  (function watchBreakpoints() {
    var mql = win.matchMedia(theme.config.mediaQuerySmall);
    theme.config.mqlSmall = mql.matches;

    var onChange = function (evt) {
      theme.config.mqlSmall = evt.matches;
      doc.dispatchEvent(new CustomEvent(evt.matches ? 'matchSmall' : 'unmatchSmall'));
    };

    if (typeof mql.addEventListener === 'function') mql.addEventListener('change', onChange);
    else if (typeof mql.addListener === 'function') mql.addListener(onChange);
  })();

  win.HoverButton = win.HoverButton || class HoverButton extends HTMLButtonElement {};
  win.MagnetButton = win.MagnetButton || class MagnetButton extends HTMLButtonElement {};
  win.HoverLink = win.HoverLink || class HoverLink extends HTMLAnchorElement {};
  win.MagnetLink = win.MagnetLink || class MagnetLink extends HTMLAnchorElement {};

  var lockCount = 0;

  class OverlayElement extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;
      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      if (event.target !== this) return;
      var id = this.getAttribute('aria-controls');
      var target = id ? doc.getElementById(id) : null;
      if (!target) return;
      if (typeof target.hide === 'function') target.hide();
      else {
        target.removeAttribute('open');
        target.hidden = true;
      }
    }
  }
  theme.safeDefine('overlay-element', OverlayElement);

  class GestureElement extends HTMLElement {}
  theme.safeDefine('gesture-element', GestureElement);

  class ModalElement extends HTMLElement {
    static get observedAttributes() {
      return ['open'];
    }

    constructor() {
      super();
      this._state = null;
      this._esc = this.onEsc.bind(this);
      this._inner = this.onInnerClick.bind(this);
    }

    get shouldLock() {
      return false;
    }

    get shouldAppendToBody() {
      return false;
    }

    get controls() {
      return this.id ? Array.from(doc.querySelectorAll('[aria-controls="' + this.id + '"]')) : [];
    }

    get focusElement() {
      return this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex^="-"])');
    }

    connectedCallback() {
      this.controls.forEach(
        function (btn) {
          btn.addEventListener('click', this.onControlClick.bind(this));
        }.bind(this)
      );
      this.addEventListener('click', this._inner);
      doc.addEventListener('keyup', this._esc);
      this.sync();
    }

    disconnectedCallback() {
      doc.removeEventListener('keyup', this._esc);
      this.removeEventListener('click', this._inner);
    }

    attributeChangedCallback() {
      this.sync();
    }

    sync() {
      var open = this.hasAttribute('open');
      if (this._state === open) return;
      this._state = open;

      this.controls.forEach(function (btn) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });

      if (open) {
        if (this.shouldAppendToBody && this.parentElement !== doc.body) {
          this._parent = this.parentElement;
          doc.body.appendChild(this);
        }

        this.hidden = false;
        this.removeAttribute('inert');
        if (this.shouldLock) {
          lockCount += 1;
          doc.body.classList.add('overflow-hidden');
        }
        this.afterShow();
      } else {
        this.setAttribute('inert', '');
        if (this.shouldLock) {
          lockCount = Math.max(0, lockCount - 1);
          if (lockCount === 0) doc.body.classList.remove('overflow-hidden');
        }
        this.afterHide();
        this.hidden = true;

        if (this._parent && this.parentElement === doc.body) {
          this._parent.appendChild(this);
          this._parent = null;
        }
      }
    }

    onControlClick(event) {
      if (event.defaultPrevented) return;
      event.preventDefault();
      if (this.hasAttribute('open')) this.hide();
      else this.show(event.currentTarget);
    }

    onInnerClick(event) {
      var close = event.target.closest('[data-close]');
      if (!close) return;
      event.preventDefault();
      this.hide();
    }

    onEsc(event) {
      if (event.key !== 'Escape') return;
      if (this.hasAttribute('open')) this.hide();
    }

    show(active) {
      this.activeElement = active || doc.activeElement;
      this.beforeShow();
      this.setAttribute('open', '');
    }

    hide() {
      this.beforeHide();
      this.removeAttribute('open');
    }

    beforeShow() {}
    beforeHide() {}

    afterShow() {
      theme.a11y.trapFocus(this, this.focusElement || this);
      this.dispatchEvent(new CustomEvent('modal:afterShow', { bubbles: true }));
    }

    afterHide() {
      theme.a11y.removeTrapFocus(this.activeElement || null);
      this.dispatchEvent(new CustomEvent('modal:afterHide', { bubbles: true }));
    }
  }

  win.ModalElement = win.ModalElement || ModalElement;
  theme.safeDefine('modal-element', ModalElement);

  class DrawerElement extends ModalElement {
    get shouldLock() {
      return true;
    }

    get shouldAppendToBody() {
      return true;
    }

    afterShow() {
      super.afterShow();
      this.dispatchEvent(new CustomEvent('drawer:afterShow', { bubbles: true }));
    }

    afterHide() {
      super.afterHide();
      this.dispatchEvent(new CustomEvent('drawer:afterHide', { bubbles: true }));
    }
  }

  win.DrawerElement = win.DrawerElement || DrawerElement;
  theme.safeDefine('drawer-element', DrawerElement);

  class XModal extends DrawerElement {}
  win.XModal = win.XModal || XModal;
  theme.safeDefine('x-modal', XModal);

  class QuickView extends XModal {
    get content() {
      return this.querySelector('.quick-view__content');
    }

    beforeShow() {
      super.beforeShow();
      this.load();
    }

    afterHide() {
      super.afterHide();
      if (this.content) this.content.innerHTML = '';
    }

    load() {
      var url = this.getAttribute('data-product-url');
      if (!url || !this.content) return;
      fetch(url.split('?')[0] + '?view=modal')
        .then(function (r) {
          return r.text();
        })
        .then(
          function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var source = parsed.querySelector('.quick-view__content');
            if (!source) return;
            this.content.innerHTML = source.innerHTML;
            this.content.querySelectorAll('script').forEach(function (oldScript) {
              var script = doc.createElement('script');
              Array.from(oldScript.attributes).forEach(function (a) {
                script.setAttribute(a.name, a.value);
              });
              script.appendChild(doc.createTextNode(oldScript.innerHTML));
              oldScript.parentNode.replaceChild(script, oldScript);
            });
          }.bind(this)
        )
        .catch(function (e) {
          console.error(e);
        });
    }
  }
  theme.safeDefine('quick-view', QuickView);

  class MenuDetails extends HTMLDetailsElement {
    connectedCallback() {
      this.summary = this.querySelector('summary');
      this.closeButton = this.querySelector('[data-close]');
      this.parentContainer = this.closest('[data-parent]');
      if (this.summary) this.summary.addEventListener('click', this.onSummaryClick.bind(this));
      if (this.closeButton) this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
    }

    onSummaryClick(event) {
      event.preventDefault();
      this.setAttribute('open', '');
      if (this.summary) this.summary.setAttribute('aria-expanded', 'true');
      if (this.parentContainer) this.parentContainer.classList.add('active');
      this.classList.add('active');
    }

    onCloseButtonClick() {
      if (this.summary) this.summary.setAttribute('aria-expanded', 'false');
      if (this.parentContainer) this.parentContainer.classList.remove('active');
      this.classList.remove('active');
      this.removeAttribute('open');
    }
  }
  theme.safeDefine('menu-details', MenuDetails, { extends: 'details' });

  class MotionList extends HTMLElement {
    connectedCallback() {
      this.setAttribute('initialized', '');
    }

    reload() {
      this.setAttribute('initialized', '');
      this.dispatchEvent(new CustomEvent('motion-list:reload', { bubbles: true }));
    }
  }
  theme.safeDefine('motion-list', MotionList);

  class SliderElement extends HTMLElement {
    connectedCallback() {
      this.selector = this.getAttribute('selector') || ':scope > *';
      this.onResize = this.update.bind(this);
      this.onScroll = debounce(this.updateFromScroll.bind(this), 50);
      this.addEventListener('scroll', this.onScroll);
      win.addEventListener('resize', this.onResize);
      this.style.scrollBehavior = theme.config.motionReduced ? 'auto' : 'smooth';
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('scroll', this.onScroll);
      win.removeEventListener('resize', this.onResize);
    }

    get items() {
      return Array.from(this.querySelectorAll(this.selector));
    }

    get currentPage() {
      return Number(this.getAttribute('data-page') || 0);
    }

    set currentPage(v) {
      this.setAttribute('data-page', String(Math.max(0, v)));
    }

    update() {
      this._items = this.items;
      var count = this._items.length;
      if (!count) {
        this.maxPage = 0;
        this.updateButtons();
        return;
      }

      var first = this._items[0];
      var itemWidth = first.getBoundingClientRect().width || 1;
      var width = this.getBoundingClientRect().width || itemWidth;
      this.perPage = Math.max(1, Math.round(width / itemWidth));
      this.maxPage = Math.max(0, Math.ceil(count / this.perPage) - 1);
      this.currentPage = Math.min(this.currentPage, this.maxPage);
      this.updateButtons();
    }

    updateFromScroll() {
      if (!this._items || !this._items.length) return;
      var first = this._items[0];
      var itemWidth = first.getBoundingClientRect().width || 1;
      this.currentPage = Math.round(this.scrollLeft / (itemWidth * this.perPage));
      this.updateButtons();
    }

    go(page, immediate) {
      if (!this._items || !this._items.length) return;
      var target = Math.max(0, Math.min(this.maxPage, page));
      var first = this._items[target * this.perPage] || this._items[this._items.length - 1];
      if (!first) return;
      this.currentPage = target;
      this.scrollTo({ left: first.offsetLeft, behavior: immediate || theme.config.motionReduced ? 'auto' : 'smooth' });
      this.updateButtons();
    }

    next() {
      this.go(this.currentPage + 1);
    }

    previous() {
      this.go(this.currentPage - 1);
    }

    updateButtons() {
      if (!this.id) return;
      var prev = doc.querySelectorAll('[aria-controls="' + this.id + '"][is="previous-button"]');
      var next = doc.querySelectorAll('[aria-controls="' + this.id + '"][is="next-button"]');
      prev.forEach(
        function (b) {
          b.disabled = this.currentPage <= 0;
        }.bind(this)
      );
      next.forEach(
        function (b) {
          b.disabled = this.currentPage >= this.maxPage;
        }.bind(this)
      );
    }
  }
  theme.safeDefine('slider-element', SliderElement);

  class PreviousButton extends HTMLButtonElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      event.preventDefault();
      var id = this.getAttribute('aria-controls');
      var slider = id ? doc.getElementById(id) : null;
      if (slider && typeof slider.previous === 'function') slider.previous();
    }
  }
  theme.safeDefine('previous-button', PreviousButton, { extends: 'button' });

  class NextButton extends HTMLButtonElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('click', this.onClick.bind(this));
    }

    onClick(event) {
      event.preventDefault();
      var id = this.getAttribute('aria-controls');
      var slider = id ? doc.getElementById(id) : null;
      if (slider && typeof slider.next === 'function') slider.next();
    }
  }
  theme.safeDefine('next-button', NextButton, { extends: 'button' });

  class TabsElement extends HTMLElement {
    connectedCallback() {
      this.controls = Array.from(this.querySelectorAll('[aria-controls]'));
      if (!this.controls.length) return;
      this.controls.forEach(
        function (c) {
          c.addEventListener('click', this.onClick.bind(this));
        }.bind(this)
      );
      this.activate(
        this.controls.find(function (c) {
          return c.getAttribute('aria-selected') === 'true' || c.classList.contains('active');
        }) || this.controls[0]
      );
    }

    onClick(event) {
      event.preventDefault();
      this.activate(event.currentTarget);
    }

    activate(control) {
      var id = control.getAttribute('aria-controls');
      this.controls.forEach(function (c) {
        var active = c === control;
        c.classList.toggle('active', active);
        c.setAttribute('aria-selected', active ? 'true' : 'false');
      });

      var container = this.parentElement || doc;
      container.querySelectorAll('[data-tab-panel], [id]').forEach(function (panel) {
        if (!panel.id) return;
        if (panel.id === id) panel.removeAttribute('hidden');
        else if (panel.hasAttribute('data-tab-panel')) panel.setAttribute('hidden', '');
      });
    }
  }
  theme.safeDefine('tabs-element', TabsElement);

  class ProgressBar extends HTMLElement {
    connectedCallback() {
      var v = parseFloat(this.getAttribute('data-value'));
      var m = parseFloat(this.getAttribute('data-max'));
      if (isFinite(v) && isFinite(m) && m > 0) {
        var p = Math.max(0, Math.min(100, (v / m) * 100));
        this.style.setProperty('--progress', p + '%');
      }
    }
  }
  theme.safeDefine('progress-bar', ProgressBar);

  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.expires = new Date(this.getAttribute('data-expires') || 0).getTime();
      this.compact = this.getAttribute('data-compact') === 'true';
      if (!this.expires || isNaN(this.expires)) return;
      this.tick();
      this.timer = setInterval(this.tick.bind(this), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      var rem = Math.max(0, this.expires - Date.now());
      var d = Math.floor(rem / 86400000);
      var h = Math.floor((rem % 86400000) / 3600000);
      var m = Math.floor((rem % 3600000) / 60000);
      var s = Math.floor((rem % 60000) / 1000);

      if (this.compact) {
        this.innerHTML =
          '<div class="countdown__item"><p>' +
          d +
          (theme.dateStrings.d || 'd') +
          '</p></div>' +
          '<div class="countdown__item"><p>' +
          h +
          (theme.dateStrings.h || 'h') +
          '</p></div>' +
          '<div class="countdown__item"><p>' +
          m +
          (theme.dateStrings.m || 'm') +
          '</p></div>' +
          '<div class="countdown__item"><p>' +
          s +
          (theme.dateStrings.s || 's') +
          '</p></div>';
      } else {
        this.innerHTML =
          '<div class="countdown__item"><p>' +
          d +
          '</p><span>' +
          (d === 1 ? theme.dateStrings.day || 'day' : theme.dateStrings.days || 'days') +
          '</span></div>' +
          '<div class="countdown__item"><p>' +
          h +
          '</p><span>' +
          (h === 1 ? theme.dateStrings.hour || 'hour' : theme.dateStrings.hours || 'hours') +
          '</span></div>' +
          '<div class="countdown__item"><p>' +
          m +
          '</p><span>' +
          (m === 1 ? theme.dateStrings.minute || 'minute' : theme.dateStrings.minutes || 'minutes') +
          '</span></div>' +
          '<div class="countdown__item"><p>' +
          s +
          '</p><span>' +
          (s === 1 ? theme.dateStrings.second || 'second' : theme.dateStrings.seconds || 'seconds') +
          '</span></div>';
      }

      if (rem <= 0) clearInterval(this.timer);
    }
  }
  theme.safeDefine('countdown-timer', CountdownTimer);

  class GMap extends HTMLElement {
    connectedCallback() {
      this.render();
    }

    setAddress(address) {
      this.setAttribute('data-map-address', address || '');
      this.render();
    }

    render() {
      var address = this.getAttribute('data-map-address');
      if (!address) return;
      var zoom = parseInt(this.getAttribute('data-zoom') || '16', 10);
      var src = 'https://www.google.com/maps?q=' + encodeURIComponent(address) + '&z=' + zoom + '&output=embed';
      this.innerHTML =
        '<iframe title="' +
        address.replace(/\"/g, '&quot;') +
        '" src="' +
        src +
        '" loading="lazy" width="100%" height="100%" style="border:0"></iframe>';
    }
  }
  theme.safeDefine('g-map', GMap);

  class GMapLocations extends HTMLUListElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick.bind(this));
    }

    get map() {
      var id = this.getAttribute('aria-controls');
      return id ? doc.getElementById(id) : null;
    }

    onClick(event) {
      var item = event.target.closest('[data-map-address]');
      if (!item || !this.contains(item)) return;
      this.querySelectorAll('[data-map-address]').forEach(function (el) {
        el.classList.toggle('active', el === item);
      });
      if (this.map && typeof this.map.setAddress === 'function') this.map.setAddress(item.getAttribute('data-map-address'));
    }
  }
  theme.safeDefine('g-map-locations', GMapLocations, { extends: 'ul' });

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (!theme.config.hasLocalStorage) {
        this.hidden = true;
        return;
      }
      this.load();
    }

    getQuery() {
      var key = (theme.settings.themeName || 'Sutera') + ':recently-viewed';
      var raw = win.localStorage.getItem(key) || '[]';
      var ids;
      try {
        ids = JSON.parse(raw);
      } catch (e) {
        ids = [];
      }
      var set = new Set(ids);
      if (this.hasAttribute('data-product-id')) set.delete(parseInt(this.getAttribute('data-product-id'), 10));
      var limit = parseInt(this.getAttribute('data-limit') || '4', 10);
      return Array.from(set)
        .slice(0, limit)
        .map(function (id) {
          return 'id:' + id;
        })
        .join(' OR ');
    }

    load() {
      var query = this.getQuery();
      if (!query) {
        var section = this.closest('.recently-section');
        if (section) section.remove();
        return;
      }

      fetch(this.getAttribute('data-url') + query)
        .then(function (r) {
          return r.text();
        })
        .then(
          function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var source = parsed.querySelector('.shopify-section recently-viewed');
            if (!source || !source.innerHTML.trim()) {
              var section = this.closest('.recently-section');
              if (section) section.remove();
              return;
            }
            this.innerHTML = source.innerHTML;
          }.bind(this)
        )
        .catch(function (e) {
          console.error(e);
        });
    }
  }
  theme.safeDefine('recently-viewed', RecentlyViewed);

  class FiltersToggle extends win.HoverButton {
    connectedCallback() {
      this.addEventListener('click', this.onClick.bind(this));
      if (theme.config.hasLocalStorage) {
        var key = (theme.settings.themeName || 'Sutera') + ':filters-toggle';
        var saved = win.localStorage.getItem(key);
        if (saved != null) this.setExpanded(saved === 'true');
      }
    }

    get target() {
      var id = this.getAttribute('aria-controls');
      return id ? doc.getElementById(id) : null;
    }

    setExpanded(expanded) {
      this.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      if (this.target) this.target.classList.toggle('xl:block', expanded);
    }

    onClick(event) {
      event.preventDefault();
      var expanded = this.getAttribute('aria-expanded') === 'true';
      this.setExpanded(!expanded);
      if (theme.config.hasLocalStorage) {
        var key = (theme.settings.themeName || 'Sutera') + ':filters-toggle';
        win.localStorage.setItem(key, String(!expanded));
      }
    }
  }
  theme.safeDefine('filters-toggle', FiltersToggle, { extends: 'button' });

  class ProductBundleInfo extends HTMLElement {}
  theme.safeDefine('product-bundle-info', ProductBundleInfo);

  class ProductBundleForm extends HTMLFormElement {
    connectedCallback() {
      this.idInput = this.querySelector('[name="id"]');
      if (this.idInput) this.idInput.disabled = false;
      this.addEventListener('submit', this.onSubmit.bind(this));
    }

    get controls() {
      var id = this.getAttribute('aria-controls');
      return id ? doc.getElementById(id) : null;
    }

    selectedVariant() {
      var selected = this.querySelector('[data-selected-variant]');
      if (selected && selected.textContent) {
        try {
          return JSON.parse(selected.textContent);
        } catch (e) {}
      }
      return { id: this.idInput ? this.idInput.value : '', price: 0, options: [] };
    }

    onSubmit(event) {
      event.preventDefault();
      var submit = this.querySelector('[type="submit"]');
      if (submit && submit.hasAttribute('aria-disabled')) return;

      var variant = this.selectedVariant();
      variant.default_featured_media = this.getAttribute('data-product-image') || '';

      (this.controls || this).dispatchEvent(
        new CustomEvent('bundle:added', {
          bubbles: true,
          detail: {
            product: this.closest('.product-card'),
            variant: variant,
          },
        })
      );
    }
  }
  theme.safeDefine('product-bundle-form', ProductBundleForm, { extends: 'form' });

  class ProductBundleRemoveButton extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick.bind(this));
    }

    get controls() {
      var id = this.getAttribute('aria-controls');
      return id ? doc.getElementById(id) : null;
    }

    onClick(event) {
      var row = event.target.closest('[data-product-bundle-variant]');
      if (!row) return;
      (this.controls || this).dispatchEvent(
        new CustomEvent('bundle:removed', { bubbles: true, detail: { variant: row } })
      );
    }
  }
  theme.safeDefine('product-bundle-remove-button', ProductBundleRemoveButton);

  class ProductBundleToggleButton extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick.bind(this));
    }

    get controls() {
      var id = this.getAttribute('aria-controls');
      return id ? doc.getElementById(id) : null;
    }

    onClick() {
      if (this.controls) this.controls.classList.toggle('active');
    }
  }
  theme.safeDefine('product-bundle-toggle-button', ProductBundleToggleButton);

  class ProductBundle extends HTMLElement {
    connectedCallback() {
      this.count = 0;
      this.submitButton = this.querySelector('[data-product-bundle-submit]');
      this.addEventListener('bundle:added', this.onAdded.bind(this));
      this.addEventListener('bundle:removed', this.onRemoved.bind(this));
      this.addEventListener('change', debounce(this.recalculate.bind(this), 100));
      if (this.submitButton) this.submitButton.addEventListener('click', this.onSubmit.bind(this));
    }

    get min() {
      return parseInt(this.getAttribute('data-minimum') || '3', 10);
    }

    get preventDuplicate() {
      return this.hasAttribute('data-prevent-duplicate');
    }

    get rows() {
      return Array.from(this.querySelectorAll('[data-product-bundle-variant]'));
    }

    get templateRow() {
      if (!this._template) {
        var first = this.querySelector('[data-product-bundle-variant][available]');
        this._template = first ? first.cloneNode(true) : null;
      }
      return this._template;
    }

    onAdded(event) {
      this.addRow(event.detail.product, event.detail.variant);
      this.recalculate();
    }

    onRemoved(event) {
      this.clearRow(event.detail.variant);
      this.recalculate();
    }

    addRow(product, variant) {
      this.count += 1;
      var row = this.querySelector('[data-product-bundle-variant][available]');
      if (!row && this.templateRow) {
        row = this.templateRow.cloneNode(true);
        var body = this.querySelector('.product-bundle__body');
        if (body) body.appendChild(row);
      }
      if (!row) return;

      var imageBox = row.querySelector('[data-product-bundle-variant-media]');
      var contentBox = row.querySelector('[data-product-bundle-variant-content]');
      var titleNode = product ? product.querySelector('[data-product-bundle-title]') : null;
      var title = titleNode ? titleNode.textContent.trim() : 'Product';

      var image =
        (variant.featured_media && variant.featured_media.preview_image && variant.featured_media.preview_image.src) ||
        variant.default_featured_media ||
        '';

      if (imageBox) {
        imageBox.innerHTML = image
          ? '<img src="' + image + '" loading="lazy" alt="' + title.replace(/\"/g, '&quot;') + '">'
          : '';
      }

      if (contentBox) {
        contentBox.innerHTML =
          '<div class="flex flex-col gap-1"><p class="horizontal-product__title font-medium text-base leading-tight">' +
          title +
          '</p></div>' +
          '<div class="price text-sm flex flex-wrap gap-1d5" data-product-bundle-variant-price data-price="' +
          (variant.price || 0) +
          '">' +
          theme.Currency.formatMoney(variant.price || 0, theme.settings.moneyFormat) +
          '</div>';
      }

      row.setAttribute('data-variant-id', variant.id);
      row.removeAttribute('available');
      row.product = product;
      if (this.preventDuplicate && product) product.setAttribute('locked', '');
    }

    clearRow(row) {
      if (!row) return;
      this.count = Math.max(0, this.count - 1);
      if (this.preventDuplicate && row.product) row.product.removeAttribute('locked');
      row.setAttribute('available', '');
      row.removeAttribute('data-variant-id');

      var image = row.querySelector('[data-product-bundle-variant-media]');
      var content = row.querySelector('[data-product-bundle-variant-content]');
      if (image) image.innerHTML = '';
      if (content) content.innerHTML = '<span class="horizontal-product__skeleton"></span><span class="horizontal-product__skeleton"></span>';

      var input = row.querySelector('quantity-input input') || row.querySelector('input[type="number"]');
      if (input) input.value = input.defaultValue || input.min || '1';
    }

    recalculate() {
      var used = this.rows.filter(function (row) {
        return !row.hasAttribute('available');
      });
      this.count = used.length;

      var subtotal = 0;
      used.forEach(function (row) {
        var price = parseInt((row.querySelector('[data-product-bundle-variant-price]') || {}).dataset.price || '0', 10);
        var quantityInput = row.querySelector('quantity-input input') || row.querySelector('input[type="number"]');
        var quantity = quantityInput ? parseInt(quantityInput.value || '1', 10) : 1;
        subtotal += (isFinite(price) ? price : 0) * (isFinite(quantity) ? quantity : 1);
      });

      var total = this.querySelector('[data-product-bundle-total]');
      var totalWithCurrency = this.querySelector('[data-product-bundle-total-with-currency]');
      if (total) total.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyFormat);
      if (totalWithCurrency) totalWithCurrency.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyWithCurrencyFormat);

      var bar = this.querySelector('[data-product-bundle-progress-bar]');
      if (bar) {
        var p = Math.max(0, Math.min(100, this.min > 0 ? (this.count / this.min) * 100 : 0));
        bar.style.setProperty('--progress', p + '%');
      }

      if (this.submitButton) this.submitButton.disabled = this.count < this.min;
    }

    onSubmit(event) {
      event.preventDefault();
      if (!this.submitButton || this.submitButton.disabled) return;

      var used = this.rows.filter(function (row) {
        return !row.hasAttribute('available');
      });
      var items = used
        .map(function (row) {
          var quantityInput = row.querySelector('quantity-input input') || row.querySelector('input[type="number"]');
          var quantity = quantityInput ? parseInt(quantityInput.value || quantityInput.defaultValue || '1', 10) : 1;
          return { id: row.getAttribute('data-variant-id'), quantity: isFinite(quantity) && quantity > 0 ? quantity : 1 };
        })
        .filter(function (item) {
          return item.id;
        });

      if (!items.length) return;

      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.setAttribute('aria-busy', 'true');

      var sections = [];
      doc.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sections } }));

      var body = JSON.stringify({ items: items, sections: sections, sections_url: win.location.pathname });
      fetch(theme.routes.cart_add_url, Object.assign(theme.utils.fetchConfig('javascript'), { body: body }))
        .then(function (r) {
          return r.json();
        })
        .then(
          function (res) {
            if (res.status) {
              this.showError(res.description || 'Unable to add bundle to cart.');
              return;
            }

            return fetch(theme.routes.cart_url, Object.assign(theme.utils.fetchConfig('json', 'GET')))
              .then(function (r) {
                return r.json();
              })
              .then(
                function (cart) {
                  cart.sections = res.sections;
                  theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle', cart: cart });
                  var drawer = doc.querySelector('cart-drawer');
                  if (drawer && typeof drawer.open === 'function') drawer.open(this.submitButton);
                }.bind(this)
              );
          }.bind(this)
        )
        .catch(function (e) {
          console.error(e);
        })
        .finally(
          function () {
            this.submitButton.removeAttribute('aria-busy');
            this.submitButton.removeAttribute('aria-disabled');
          }.bind(this)
        );
    }

    showError(message) {
      this.error = this.error || this.querySelector('.product-form__error-message');
      if (!this.error) return;
      this.error.innerText = message || '';
      this.error.toggleAttribute('hidden', !message);
    }
  }
  theme.safeDefine('product-bundle', ProductBundle);

  theme.supportsPassive = false;
  try {
    var passiveOptions = Object.defineProperty({}, 'passive', {
      get: function () {
        theme.supportsPassive = true;
      },
    });
    win.addEventListener('testPassive', null, passiveOptions);
    win.removeEventListener('testPassive', null, passiveOptions);
  } catch (e) {}

  if (!theme.Animation) {
    theme.Animation = class Animation {
      constructor(container) {
        this.container = container;
      }

      get type() {
        return this.container.hasAttribute('data-animate') ? this.container.getAttribute('data-animate') : 'fade-up';
      }

      get delay() {
        return this.container.hasAttribute('data-animate-delay') ? parseInt(this.container.getAttribute('data-animate-delay'), 10) / 1000 : 0;
      }

      get paused() {
        return this.container.hasAttribute('paused');
      }

      beforeLoad() {
        if (this.type === 'none' || this.paused) return;
        if (this.type === 'fade-in') {
          this.container.style.opacity = '0';
        } else if (this.type === 'fade-up') {
          this.container.style.transform = 'translateY(min(2rem, 90%))';
          this.container.style.opacity = '0';
        } else if (this.type === 'fade-up-large') {
          this.container.style.transform = 'translateY(90%)';
          this.container.style.opacity = '0';
        } else if (this.type === 'zoom-out') {
          this.container.style.transform = 'scale(1.3)';
        }
      }

      load() {
        if (this.type === 'none' || this.paused) return Promise.resolve();
        var props = {};
        if (this.type === 'fade-in') {
          props = { opacity: 1 };
        } else if (this.type === 'fade-up' || this.type === 'fade-up-large') {
          props = { transform: 'translateY(0)', opacity: 1 };
        } else if (this.type === 'zoom-out') {
          props = { transform: 'scale(1)' };
        }

        return Motion.animate(this.container, props, { duration: 0.6, delay: this.delay }).finished.then(
          function () {
            this.container.classList.add('animate');
            this.container.removeAttribute('paused');
          }.bind(this)
        );
      }

      reset() {
        this.container.classList.remove('animate');
        this.beforeLoad();
        return Promise.resolve();
      }

      reload() {
        this.container.removeAttribute('paused');
        this.beforeLoad();
        return this.load();
      }
    };
  }

  class AnimateElement extends HTMLElement {
    connectedCallback() {
      if (theme.config.motionReduced) return;
      if (this.__animationReady) return;
      this.__animationReady = true;
      this.animation = new theme.Animation(this);
      this.animation.beforeLoad();

      if (this.hasAttribute('data-immediate')) {
        this.animation.load();
        return;
      }

      Motion.inView(
        this,
        function () {
          this.animation.load();
        }.bind(this)
      );
    }
  }
  theme.safeDefine('animate-element', AnimateElement);

  class AnimatePicture extends HTMLPictureElement {
    connectedCallback() {
      if (theme.config.motionReduced) return;
      if (this.__animationReady) return;
      this.__animationReady = true;
      this.animation = new theme.Animation(this);
      this.animation.beforeLoad();

      if (this.hasAttribute('data-immediate')) {
        this.animation.load();
        return;
      }

      Motion.inView(
        this,
        function () {
          this.animation.load();
        }.bind(this)
      );
    }
  }
  theme.safeDefine('animate-picture', AnimatePicture, { extends: 'picture' });

  class SplitWords extends HTMLElement {
    connectedCallback() {
      if (this.__splitReady) return;
      this.__splitReady = true;

      var text = (this.textContent || '').trim();
      if (!text) return;
      var animateType = this.getAttribute('data-animate') || 'fade-up-large';
      var baseDelay = parseInt(this.getAttribute('data-animate-delay') || '0', 10);
      var words = text.split(/\s+/);

      this.textContent = '';
      words.forEach(
        function (word, index) {
          var wordWrap = doc.createElement('span');
          wordWrap.className = 'word';

          var animateWrap = doc.createElement('animate-element');
          animateWrap.className = 'block';
          animateWrap.setAttribute('data-animate', animateType);
          animateWrap.setAttribute('data-animate-delay', String(baseDelay + index * 30));
          animateWrap.textContent = word;
          wordWrap.appendChild(animateWrap);
          this.appendChild(wordWrap);

          if (index < words.length - 1) {
            var whitespace = doc.createElement('span');
            whitespace.className = 'whitespace';
            whitespace.textContent = '\u00A0';
            whitespace.setAttribute('aria-hidden', 'true');
            this.appendChild(whitespace);
          }
        }.bind(this)
      );
    }
  }
  theme.safeDefine('split-words', SplitWords);

  class MediaElement extends HTMLElement {
    connectedCallback() {
      this.parallax = this.hasAttribute('data-parallax') ? parseFloat(this.getAttribute('data-parallax')) : 0;
      this.direction = this.getAttribute('data-parallax-dir') || 'vertical';
      if (theme.config.motionReduced || !this.parallax) return;
      this.updateParallax = this.updateParallax.bind(this);
      this.onScroll = throttle(this.updateParallax, 20);
      win.addEventListener('scroll', this.onScroll, { passive: true });
      win.addEventListener('resize', this.onScroll);
      this.updateParallax();
    }

    disconnectedCallback() {
      if (this.onScroll) {
        win.removeEventListener('scroll', this.onScroll);
        win.removeEventListener('resize', this.onScroll);
      }
    }

    get media() {
      return Array.from(this.querySelectorAll('picture>img, video, iframe, svg, video-media, g-map'));
    }

    updateParallax() {
      var rect = this.getBoundingClientRect();
      var vh = win.innerHeight || doc.documentElement.clientHeight || 1;
      var progress = (vh - rect.top) / (vh + rect.height);
      progress = Math.max(0, Math.min(1, progress));
      var scale = 1 + this.parallax;
      var amount = (this.parallax * 100) / (1 + this.parallax);
      var shift = (progress * 2 - 1) * amount;

      this.media.forEach(
        function (element) {
          if (this.direction === 'horizontal') {
            element.style.transform = 'scale(' + scale + ') translateX(' + shift + '%)';
          } else if (this.direction === 'scale') {
            var dynamicScale = 1 + this.parallax * progress;
            element.style.transform = 'scale(' + dynamicScale + ')';
          } else {
            element.style.transform = 'scale(' + scale + ') translateY(' + shift + '%)';
          }
        }.bind(this)
      );
    }
  }
  theme.safeDefine('media-element', MediaElement);

  class MarqueeElement extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;
      if (theme.config.motionReduced) return;

      this.speed = this.hasAttribute('data-speed') ? parseInt(this.getAttribute('data-speed'), 10) : 16;
      this.direction = this.getAttribute('data-direction') || 'left';

      if (!this.firstElementChild || this.childElementCount !== 1) return;
      this.firstElementChild.classList.add('animate');
      var childWidth = this.firstElementChild.getBoundingClientRect().width || 1;
      var parentWidth = this.getBoundingClientRect().width || childWidth;
      var maximum = Math.ceil(parentWidth / childWidth) + 2;

      for (var i = 0; i < maximum; i++) {
        var clone = this.firstElementChild.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        this.appendChild(clone);
      }

      var duration = (33 - this.speed) * Math.min(2.5, Math.ceil(childWidth / Math.max(1, parentWidth)));
      this.style.setProperty('--duration', duration + 's');
      if (this.direction === 'right') this.classList.add('scrolling-text--right');

      this.__io = new IntersectionObserver(
        function (entries) {
          entries.forEach(
            function (entry) {
              this.classList.toggle('paused', !entry.isIntersecting);
            }.bind(this)
          );
        }.bind(this),
        { rootMargin: '0px 0px 50px 0px' }
      );
      this.__io.observe(this);
    }

    disconnectedCallback() {
      if (this.__io) this.__io.disconnect();
    }
  }
  theme.safeDefine('marquee-element', MarqueeElement);

  class LogoList extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;
      if (theme.config.motionReduced) return;

      this.speed = this.hasAttribute('data-speed') ? parseInt(this.getAttribute('data-speed'), 10) : 16;
      this.direction = this.getAttribute('data-direction') || 'left';
      this.bar = this.querySelector('.logo-bar');
      if (!this.bar) return;

      this.bar.classList.add('animate');
      var childWidth = this.bar.getBoundingClientRect().width || 1;
      var parentWidth = this.getBoundingClientRect().width || childWidth;
      var maximum = Math.ceil(parentWidth / childWidth) + 2;
      for (var i = 0; i < maximum; i++) {
        var clone = this.bar.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        this.appendChild(clone);
      }
      var duration = (33 - this.speed) * Math.min(2.5, Math.ceil(childWidth / Math.max(1, parentWidth)));
      this.style.setProperty('--duration', duration + 's');
      if (this.direction === 'right') this.classList.add('logo-list--right');
    }
  }
  theme.safeDefine('logo-list', LogoList);

  class TextScrolling extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.children);
      if (this.items.length < 2) return;
      this.index = 0;
      this.update();
      this.timer = setInterval(
        function () {
          this.index = (this.index + 1) % this.items.length;
          this.update();
        }.bind(this),
        3500
      );
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    update() {
      this.items.forEach(
        function (item, index) {
          var active = index === this.index;
          item.setAttribute('aria-current', active ? 'true' : 'false');
          item.style.opacity = active ? '1' : '0.15';
        }.bind(this)
      );
    }
  }
  theme.safeDefine('text-scrolling', TextScrolling);

  class ScrolledImages extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;
      this.beforeInit();
    }

    get template() {
      return this.querySelector('template');
    }

    get images() {
      return this.template ? Array.from(this.template.content.querySelectorAll('.scrolled-images__item')) : [];
    }

    beforeInit() {
      var main = this.querySelector('.scrolled-images__main');
      if (!main || !this.template || !this.images.length || main.children.length) return;

      for (var i = 0; i < 3; i++) {
        var images = this.images.slice().sort(function () {
          return Math.random() - 0.5;
        });
        while (images.length < 8 && images.length > 0) {
          images.push(images[images.length % this.images.length].cloneNode(true));
        }
        var row = doc.createElement('div');
        row.className = 'scrolled-images__row';
        images.forEach(function (item) {
          row.appendChild(item.cloneNode(true));
        });
        main.appendChild(row);
      }
    }
  }
  theme.safeDefine('scrolled-images', ScrolledImages);

  class LazyBackground extends HTMLElement {
    connectedCallback() {
      if (this.__loaded) return;
      Motion.inView(
        this,
        function () {
          this.load();
        }.bind(this),
        { margin: '300px 0px 300px 0px' }
      );
    }

    load() {
      if (this.__loaded) return;
      this.__loaded = true;
      var bg = this.style.backgroundImage || '';
      if (!bg) return;
      this.style.backgroundImage = bg
        .replace(/([?&])width=1([&#"]?)/gi, '$1width=1600$2')
        .replace(/(_1x1\.)/gi, '_1600x.');
    }
  }
  theme.safeDefine('lazy-background', LazyBackground);

  class ParallaxOverlay extends HTMLElement {
    connectedCallback() {
      this.target = this.getAttribute('data-target') || 'opacity';
      this.start = parseFloat(this.getAttribute('data-start') || '1');
      this.stop = parseFloat(this.getAttribute('data-stop') || '1');
      this.onScroll = throttle(this.update.bind(this), 20);
      win.addEventListener('scroll', this.onScroll, { passive: true });
      win.addEventListener('resize', this.onScroll);
      this.update();
    }

    disconnectedCallback() {
      if (this.onScroll) {
        win.removeEventListener('scroll', this.onScroll);
        win.removeEventListener('resize', this.onScroll);
      }
    }

    update() {
      var root = this.parentElement || this;
      var rect = root.getBoundingClientRect();
      var vh = win.innerHeight || doc.documentElement.clientHeight || 1;
      var progress = (vh - rect.top) / (vh + rect.height);
      progress = Math.max(0, Math.min(1, progress));
      var value = this.start + (this.stop - this.start) * progress;
      if (this.target === 'opacity') this.style.opacity = String(value);
      else this.style[this.target] = String(value);
    }
  }
  theme.safeDefine('parallax-overlay', ParallaxOverlay);

  class AccordionDetails extends HTMLDetailsElement {
    constructor() {
      super();
      this._open = this.hasAttribute('open');
      this.summaryElement = this.querySelector('summary');
      this.setAttribute('aria-expanded', this._open ? 'true' : 'false');
    }

    connectedCallback() {
      if (this.summaryElement && !this.__bound) {
        this.__bound = true;
        this.summaryElement.addEventListener('click', this.onSummaryClick.bind(this));
      }
    }

    get controlledElement() {
      return this.closest('accordions-details');
    }

    static get observedAttributes() {
      return ['open'];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'open' || oldValue === newValue) return;
      this._open = newValue === '';
      this.setAttribute('aria-expanded', this._open ? 'true' : 'false');
      this.dispatchEventHandler();
    }

    get open() {
      return this._open;
    }

    set open(value) {
      var boolValue = !!value;
      if (boolValue === this._open) return;
      this._open = boolValue;
      if (boolValue) this.setAttribute('open', '');
      else this.removeAttribute('open');
      this.setAttribute('aria-expanded', boolValue ? 'true' : 'false');
      this.dispatchEventHandler();
    }

    onSummaryClick(event) {
      event.preventDefault();
      this.open = !this.open;
    }

    close() {
      this.open = false;
    }

    dispatchEventHandler() {
      (this.controlledElement || this).dispatchEvent(
        new CustomEvent('toggle', { bubbles: true, detail: { current: this, open: this.open } })
      );
    }
  }
  theme.safeDefine('accordion-details', AccordionDetails, { extends: 'details' });

  class AccordionsDetails extends HTMLElement {
    constructor() {
      super();
      this.onToggle = this.onToggle.bind(this);
    }

    connectedCallback() {
      this.addEventListener('toggle', this.onToggle);
    }

    disconnectedCallback() {
      this.removeEventListener('toggle', this.onToggle);
    }

    get items() {
      return Array.from(this.querySelectorAll('details[is="accordion-details"]'));
    }

    onToggle(event) {
      if (!event.detail || !event.detail.open) return;
      var target = event.detail.current;
      this.items.forEach(function (item) {
        if (item !== target && typeof item.close === 'function') item.close();
      });

      var header = doc.querySelector('header.header');
      var headerHeight = !theme.config.mqlSmall && header ? Math.round(header.clientHeight) : 0;
      setTimeout(function () {
        win.scrollTo({
          top: target.getBoundingClientRect().top + win.scrollY - headerHeight,
          behavior: theme.config.motionReduced ? 'auto' : 'smooth',
        });
      }, 120);
    }
  }
  theme.safeDefine('accordions-details', AccordionsDetails);

  var cachedSectionsRenderingAPI = new Map();
  class APIButton extends HTMLButtonElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;
      Motion.inView(this, this.init.bind(this));
    }

    get sectionId() {
      return this.getAttribute('data-section-id');
    }

    get sectionRenderingId() {
      return this.getAttribute('data-id');
    }

    init() {
      if (!this.sectionId || !this.sectionRenderingId) return;
      var target = doc.getElementById(this.sectionRenderingId);
      if (!target || target.hasAttribute('loaded')) return;

      var url = (theme.routes.root_url || '/') + '?section_id=' + this.sectionId;
      if (cachedSectionsRenderingAPI.has(url)) this.renderSectionFromCache(url);
      else this.renderSectionFromFetch(url);

      this.sectionsRenderingAPIListener = this.afterSectionsRenderingAPI.bind(this);
      doc.addEventListener('sectionsRenderingAPI:cached', this.sectionsRenderingAPIListener);
    }

    renderSectionFromCache(url) {
      var responseText = cachedSectionsRenderingAPI.get(url);
      if (responseText && responseText.length) this.updateSectionRendering(responseText);
    }

    renderSectionFromFetch(url) {
      cachedSectionsRenderingAPI.set(url, '');
      fetch(url)
        .then(function (response) {
          return response.text();
        })
        .then(
          function (responseText) {
            this.updateSectionRendering(responseText);
            cachedSectionsRenderingAPI.set(url, responseText);

            doc.dispatchEvent(
              new CustomEvent('sectionsRenderingAPI:cached', {
                detail: { url: url, sectionId: this.sectionId },
              })
            );
          }.bind(this)
        )
        .catch(function (e) {
          console.error(e);
        });
    }

    updateSectionRendering(responseText) {
      var parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
      var replacement = parsedHTML.getElementById(this.sectionRenderingId);
      var current = doc.getElementById(this.sectionRenderingId);
      if (replacement && current) current.replaceWith(replacement);
      doc.removeEventListener('sectionsRenderingAPI:cached', this.sectionsRenderingAPIListener);
    }

    afterSectionsRenderingAPI(event) {
      if (event.detail && event.detail.sectionId === this.sectionId) {
        this.renderSectionFromCache(event.detail.url);
      }
    }
  }
  theme.safeDefine('api-button', APIButton, { extends: 'button' });

  class CarouselElement extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;
      this.selectedIndex = parseInt(this.getAttribute('initial-index') || '0', 10) || 0;
      this.onResize = throttle(this.refresh.bind(this), 120);
      win.addEventListener('resize', this.onResize);
      this.addEventListener(
        'control:select',
        function (event) {
          if (!event.detail) return;
          this.select(event.detail.index);
        }.bind(this)
      );
      this.refresh();
    }

    disconnectedCallback() {
      win.removeEventListener('resize', this.onResize);
    }

    get items() {
      return Array.from(this.children).filter(function (child) {
        return !child.classList.contains('flickity-page-dots');
      });
    }

    shouldEnable() {
      if (this.items.length <= 1) return false;
      if (this.classList.contains('text-with-icons')) return win.matchMedia('(max-width: 1023px)').matches;
      if (this.classList.contains('testimonials')) return true;
      if (this.closest('shop-the-look')) return true;
      if (this.hasAttribute('watch-css')) {
        var content = win.getComputedStyle(this, ':after').content || '';
        return content.indexOf('flickity') > -1;
      }
      return win.matchMedia('(max-width: 767px)').matches;
    }

    refresh() {
      this.enabled = this.shouldEnable();
      this.renderDots();
      this.select(this.selectedIndex, true);
    }

    renderDots() {
      if (!this.enabled || this.items.length <= 1) {
        if (this.dots) {
          this.dots.remove();
          this.dots = null;
        }
        return;
      }

      if (!this.dots) {
        this.dots = doc.createElement('ol');
        this.dots.className = 'flickity-page-dots';
        this.appendChild(this.dots);
      }
      this.dots.innerHTML = '';
      this.items.forEach(
        function (_item, index) {
          var dot = doc.createElement('li');
          dot.className = 'flickity-page-dot';
          dot.tabIndex = 0;
          dot.setAttribute('aria-label', 'Slide ' + (index + 1));
          dot.addEventListener(
            'click',
            function () {
              this.select(index);
            }.bind(this)
          );
          this.dots.appendChild(dot);
        }.bind(this)
      );
    }

    select(index, immediate) {
      var items = this.items;
      if (!items.length) return;
      var max = items.length - 1;
      if (index < 0) index = max;
      if (index > max) index = 0;
      this.selectedIndex = index;

      items.forEach(
        function (item, itemIndex) {
          var active = itemIndex === this.selectedIndex;
          item.classList.toggle('is-selected', active);
          if (this.enabled) {
            item.hidden = !active;
            item.style.display = active ? '' : 'none';
          } else {
            item.hidden = false;
            item.style.display = '';
          }
        }.bind(this)
      );

      if (this.dots) {
        Array.from(this.dots.children).forEach(
          function (dot, dotIndex) {
            var active = dotIndex === this.selectedIndex;
            dot.classList.toggle('is-selected', active);
            dot.setAttribute('aria-current', active ? 'true' : 'false');
          }.bind(this)
        );
      }

      if (!immediate) {
        this.dispatchEvent(new CustomEvent('carousel:change', { bubbles: true, detail: { index: this.selectedIndex } }));
      }
    }

    next() {
      this.select(this.selectedIndex + 1);
    }

    previous() {
      this.select(this.selectedIndex - 1);
    }
  }
  theme.safeDefine('carousel-element', CarouselElement);

  class TestimonialsElement extends CarouselElement {
    connectedCallback() {
      super.connectedCallback();
      if (this.items.length > 1) {
        this.timer = setInterval(
          function () {
            this.next();
          }.bind(this),
          6000
        );
      }
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      clearInterval(this.timer);
    }
  }
  theme.safeDefine('testimonials-element', TestimonialsElement);

  class LookbookElement extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;
      Motion.inView(
        this,
        function () {
          var images = this.media;
          if (!images.length) {
            this.init();
            return;
          }
          Promise.all(
            images.map(function (image) {
              return theme.utils.imageLoaded(image);
            })
          ).finally(
            function () {
              this.init();
            }.bind(this)
          );
        }.bind(this)
      );
    }

    get media() {
      return Array.from(this.querySelectorAll('img, svg'));
    }

    get items() {
      return Array.from(this.querySelectorAll('.hotspot'));
    }

    init() {
      if (this.__initialized) return;
      this.__initialized = true;

      this.items.forEach(
        function (item, index) {
          item.addEventListener(
            'mouseenter',
            function () {
              this.select(index);
            }.bind(this)
          );
          item.addEventListener(
            'click',
            function () {
              this.select(index);
              this.open(index);
            }.bind(this)
          );
        }.bind(this)
      );

      if (this.items.length) {
        this.select(0);
        setTimeout(
          function () {
            this.animate();
          }.bind(this),
          400
        );
      }
    }

    open(selectedIndex) {
      this.items.forEach(function (item, index) {
        item.classList.toggle('active', selectedIndex === index);
      });
    }

    openAll() {
      this.items.forEach(function (item) {
        item.classList.add('active');
      });
    }

    closeAll() {
      this.items.forEach(function (item) {
        item.classList.remove('active');
      });
      clearTimeout(this.timer);
    }

    animate() {
      this.openAll();
      clearTimeout(this.timer);
      this.timer = setTimeout(
        function () {
          this.closeAll();
        }.bind(this),
        3000
      );
    }

    select(selectedIndex) {
      this.items.forEach(function (item, index) {
        item.setAttribute('aria-current', selectedIndex === index ? 'true' : 'false');
      });
      this.dispatchEvent(new CustomEvent('lookbook:change', { bubbles: true, detail: { index: selectedIndex } }));
    }
  }
  theme.safeDefine('lookbook-element', LookbookElement);

  class ShopTheLook extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;
      if (!this.lookbook || !this.carousel) return;

      this.lookbook.addEventListener(
        'lookbook:change',
        function (event) {
          if (event.detail) this.carousel.select(event.detail.index);
        }.bind(this)
      );
      this.carousel.addEventListener(
        'carousel:change',
        function (event) {
          if (event.detail) this.lookbook.select(event.detail.index);
        }.bind(this)
      );
    }

    get lookbook() {
      return this.querySelector('lookbook-element');
    }

    get carousel() {
      return this.querySelector('carousel-element');
    }
  }
  theme.safeDefine('shop-the-look', ShopTheLook);

  class SocialFeed extends XModal {
    get lookbook() {
      return this.querySelector('lookbook-element');
    }

    beforeShow() {
      super.beforeShow();
      if (this.lookbook && typeof this.lookbook.closeAll === 'function') this.lookbook.closeAll();
    }

    afterShow() {
      super.afterShow();
      if (!this.lookbook) return;
      Motion.inView(this.lookbook, this.lookbook.animate.bind(this.lookbook));
    }
  }
  theme.safeDefine('social-feed', SocialFeed);

  class SocialFeedButton extends APIButton {
    connectedCallback() {
      super.connectedCallback();
      if (this.__bound) return;
      this.__bound = true;
      this.addEventListener(
        'click',
        function () {
          var target = this.hasAttribute('aria-controls') ? doc.getElementById(this.getAttribute('aria-controls')) : null;
          if (target && typeof target.show === 'function') target.show(this);
        }.bind(this)
      );
    }
  }
  theme.safeDefine('social-feed-button', SocialFeedButton, { extends: 'button' });

  class ScrollShadow extends HTMLElement {
    connectedCallback() {
      this.onScroll = throttle(this.update.bind(this), 50);
      this.addEventListener('scroll', this.onScroll, { passive: true });
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('scroll', this.onScroll);
    }

    update() {
      var atStart = this.scrollTop <= 1 && this.scrollLeft <= 1;
      var atEndY = Math.ceil(this.scrollTop + this.clientHeight) >= this.scrollHeight - 1;
      var atEndX = Math.ceil(this.scrollLeft + this.clientWidth) >= this.scrollWidth - 1;
      this.toggleAttribute('data-at-start', atStart);
      this.toggleAttribute('data-at-end', atEndY && atEndX);
    }
  }
  theme.safeDefine('scroll-shadow', ScrollShadow);

  class ImageComparison extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;
      Motion.inView(
        this,
        function () {
          this.init();
        }.bind(this)
      );
    }

    get button() {
      return this.querySelector('button');
    }

    get horizontal() {
      return this.getAttribute('data-layout') === 'horizontal';
    }

    init() {
      if (!this.button) return;
      this.active = false;
      this.setAttribute('animate', '');
      this.classList.add('animating');
      setTimeout(
        function () {
          this.classList.remove('animating');
        }.bind(this),
        800
      );

      this.startHandler = this.startHandler.bind(this);
      this.endHandler = this.endHandler.bind(this);
      this.moveHandler = this.moveHandler.bind(this);

      this.button.addEventListener('touchstart', this.startHandler, theme.supportsPassive ? { passive: false } : false);
      doc.body.addEventListener('touchend', this.endHandler, theme.supportsPassive ? { passive: true } : false);
      doc.body.addEventListener('touchmove', this.moveHandler, theme.supportsPassive ? { passive: true } : false);
      this.button.addEventListener('mousedown', this.startHandler);
      doc.body.addEventListener('mouseup', this.endHandler);
      doc.body.addEventListener('mousemove', this.moveHandler);
    }

    disconnectedCallback() {
      if (!this.startHandler) return;
      this.button && this.button.removeEventListener('touchstart', this.startHandler);
      this.button && this.button.removeEventListener('mousedown', this.startHandler);
      doc.body.removeEventListener('touchend', this.endHandler);
      doc.body.removeEventListener('touchmove', this.moveHandler);
      doc.body.removeEventListener('mouseup', this.endHandler);
      doc.body.removeEventListener('mousemove', this.moveHandler);
    }

    startHandler(event) {
      event.preventDefault();
      this.active = true;
      this.classList.add('scrolling');
    }

    endHandler() {
      this.active = false;
      this.classList.remove('scrolling');
    }

    moveHandler(rawEvent) {
      if (!this.active) return;
      var event = (rawEvent.touches && rawEvent.touches[0]) || rawEvent;
      var bounds = this.getBoundingClientRect();
      var point = this.horizontal ? event.pageX - (bounds.left + win.scrollX) : event.pageY - (bounds.top + win.scrollY);
      this.scrollToPoint(point);
    }

    scrollToPoint(point) {
      var distance = this.horizontal ? this.clientWidth : this.clientHeight;
      if (!distance) return;
      var max = distance - 20;
      var min = 20;
      var clamped = Math.max(min, Math.min(point, max));
      var percent = (clamped * 100) / distance;
      this.style.setProperty('--percent', percent + '%');
    }
  }
  theme.safeDefine('image-comparison', ImageComparison);

  class RevealBanner extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;
      this.onScroll = throttle(this.update.bind(this), 20);
      win.addEventListener('scroll', this.onScroll, { passive: true });
      win.addEventListener('resize', this.onScroll);
      this.update();
    }

    disconnectedCallback() {
      if (this.onScroll) {
        win.removeEventListener('scroll', this.onScroll);
        win.removeEventListener('resize', this.onScroll);
      }
    }

    update() {
      var tracker = this.querySelector('.reveal-banner__tracker') || this;
      var rect = tracker.getBoundingClientRect();
      var vh = win.innerHeight || doc.documentElement.clientHeight || 1;
      var progress = (vh - rect.top) / (vh + rect.height);
      progress = Math.max(0, Math.min(1, progress));

      var inset = 20 - progress * 20;
      var side = 35 - progress * 35;
      this.querySelectorAll('.banner__media').forEach(function (media) {
        media.style.clipPath = 'inset(' + inset + '% ' + side + '% 0% ' + side + '% round var(--rounded-block))';
      });

      var tracker2 = this.querySelector('.reveal-banner__tracker2nd');
      if (tracker2) {
        var rect2 = tracker2.getBoundingClientRect();
        var progress2 = (vh - rect2.top) / (vh + rect2.height);
        progress2 = Math.max(0, Math.min(1, progress2));
        var overlay = this.querySelector('.banner__overlay');
        var content = this.querySelector('.banner__content');
        if (overlay) overlay.style.opacity = String(progress2);
        if (content) {
          content.style.opacity = String(progress2);
          content.style.visibility = progress2 > 0.05 ? 'visible' : 'hidden';
          content.style.transform = 'translateY(' + (10 - progress2 * 10) + '%)';
        }
      }
    }
  }
  theme.safeDefine('reveal-banner', RevealBanner);

  class SplittingBanner extends RevealBanner {
    connectedCallback() {
      if (this.__splitReady) return;
      this.__splitReady = true;
      super.connectedCallback();
      this.wrapChars();
    }

    wrapChars() {
      var target = this.querySelector('.splitting');
      if (!target || target.hasAttribute('data-chars-ready')) return;
      var text = target.textContent || '';
      target.textContent = '';
      text.split('').forEach(function (char) {
        var span = doc.createElement('span');
        span.className = 'char';
        span.style.opacity = char.trim() ? '0.3' : '1';
        span.textContent = char;
        target.appendChild(span);
      });
      target.setAttribute('data-chars-ready', '');
    }
  }
  theme.safeDefine('splitting-banner', SplittingBanner);

  class ScrollingBanner extends HTMLElement {
    connectedCallback() {
      if (this.__ready) return;
      this.__ready = true;
      this.mediaBlocks = Array.from(this.querySelectorAll('.image-with-text__image > .media[aria-controls]'));
      this.contentBlocks = Array.from(this.querySelectorAll('.image-with-text__content[id]'));
      this.onScroll = throttle(this.update.bind(this), 30);
      win.addEventListener('scroll', this.onScroll, { passive: true });
      win.addEventListener('resize', this.onScroll);
      this.update();
    }

    disconnectedCallback() {
      if (this.onScroll) {
        win.removeEventListener('scroll', this.onScroll);
        win.removeEventListener('resize', this.onScroll);
      }
    }

    update() {
      if (!this.contentBlocks.length) return;
      var rect = this.getBoundingClientRect();
      var vh = win.innerHeight || doc.documentElement.clientHeight || 1;
      var progress = (vh - rect.top) / (vh + rect.height);
      progress = Math.max(0, Math.min(0.999, progress));
      var index = Math.min(this.contentBlocks.length - 1, Math.floor(progress * this.contentBlocks.length));
      this.select(index);
    }

    select(index) {
      this.contentBlocks.forEach(function (item, itemIndex) {
        var active = itemIndex === index;
        item.style.opacity = active ? '1' : '0';
        item.style.pointerEvents = active ? 'auto' : 'none';
      });

      this.mediaBlocks.forEach(function (media, mediaIndex) {
        var active = mediaIndex <= index;
        media.style.clipPath = active ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%)' : 'polygon(0 100%, 100% 100%, 100% 100%, 0 100%)';
      });
    }
  }
  theme.safeDefine('scrolling-banner', ScrollingBanner);

  function noop(name, base, options) {
    if (customElements.get(name)) return;
    theme.safeDefine(name, class extends (base || HTMLElement) {}, options);
  }

  noop('sticky-element');
  noop('parallax-element', HTMLDivElement, { extends: 'div' });
  noop('custom-select', HTMLSelectElement, { extends: 'select' });
  noop('hover-button', win.HoverButton, { extends: 'button' });
  noop('magnet-button', win.MagnetButton, { extends: 'button' });
  noop('hover-link', win.HoverLink, { extends: 'a' });
  noop('magnet-link', win.MagnetLink, { extends: 'a' });
  noop('hover-element');
  noop('magnet-element');
  noop('reveal-link', HTMLAnchorElement, { extends: 'a' });
  noop('quantity-label', HTMLLabelElement, { extends: 'label' });
  noop('product-bundle-details', HTMLDetailsElement, { extends: 'details' });
  noop('control-button', HTMLButtonElement, { extends: 'button' });

  theme.DOMready(function () {
    if (doc.body) doc.body.classList.add('loaded');
    if (doc.body && theme.config.isTouch) doc.body.classList.add('touch');
    theme.utils.setScrollbarWidth();
    theme.utils.externalLinksNewTab();
  });
})();
